import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { LucideIcon } from "lucide-react";
import {
  Check,
  Circle,
  Copy,
  Hand,
  LayoutGrid,
  MessageSquare,
  MonitorOff,
  MonitorUp,
  MoreHorizontal,
  PanelRight,
  PictureInPicture,
  PictureInPicture2,
  Settings,
  Square,
  Users,
  X,
} from "lucide-react";
import type { SidePanelTab, ViewMode } from "./MeetingControls";
import { useCallPip } from "../lib/callPipContext";
import { RECORDING_ENABLED } from "../lib/featureFlags";

interface MeetingControlsOverflowProps {
  /** Only rendered below desktop. Controls popover (compact) vs sheet (mobile). */
  tier: "compact" | "mobile";
  activePanel: SidePanelTab;
  onPanelChange: (next: SidePanelTab) => void;
  chatUnreadCount: number;
  /** Participant count for the people tile label. */
  participantBadge: number;
  screenSharing: boolean;
  screenShareDisabled: boolean;
  screenShareNotSupported?: boolean;
  onToggleScreenShare: () => void;
  reactionEmojis: readonly string[];
  onEmitReaction: (emoji: string) => void;
  viewMode: ViewMode;
  onViewModeChange: (next: ViewMode) => void;
  handRaised: boolean;
  onToggleHandRaised: () => void;
  isHost?: boolean;
  recording?: boolean;
  onToggleRecording?: () => void;
  onOpenSettings?: () => void;
}

const LAYOUT_TABS: {
  mode: ViewMode;
  icon: typeof LayoutGrid;
  shortKey: string;
}[] = [
  { mode: "grid", icon: LayoutGrid, shortKey: "call.controls.layoutGridShort" },
  { mode: "speaker", icon: PictureInPicture2, shortKey: "call.controls.layoutSpeakerShort" },
  { mode: "sidebar", icon: PanelRight, shortKey: "call.controls.layoutSidebarShort" },
];

function getLayoutOptions(t: TFunction): { mode: ViewMode; label: string }[] {
  return [
    { mode: "grid", label: t("call.controls.layoutGrid") },
    { mode: "speaker", label: t("call.controls.layoutSpeaker") },
    { mode: "sidebar", label: t("call.controls.layoutSidebar") },
  ];
}

export function MeetingControlsOverflow({
  tier,
  activePanel,
  onPanelChange,
  chatUnreadCount,
  participantBadge,
  screenSharing,
  screenShareDisabled,
  screenShareNotSupported = false,
  onToggleScreenShare,
  reactionEmojis,
  onEmitReaction,
  viewMode,
  onViewModeChange,
  handRaised,
  onToggleHandRaised,
  isHost = false,
  recording = false,
  onToggleRecording,
  onOpenSettings,
}: MeetingControlsOverflowProps) {
  const { t } = useTranslation();
  const pip = useCallPip();
  const showPip = pip !== null && pip.tier !== "none";
  // open() runs first, straight from the click, so the browser keeps the gesture.
  const togglePip = () => {
    if (!pip) return;
    if (pip.isOpen) pip.close();
    else void pip.open();
    close();
  };
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const isMobile = tier === "mobile";
  const screenShareLabel = screenSharing ? t("call.controls.stopScreenShare") : t("call.controls.shareScreen");

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (wrapRef.current && target && !wrapRef.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleMouseDown);
    return () => document.removeEventListener("pointerdown", handleMouseDown);
  }, [open]);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);

  const close = () => setOpen(false);
  const togglePanel = (tab: NonNullable<SidePanelTab>) => {
    onPanelChange(activePanel === tab ? null : tab);
    close();
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      // clipboard rejects on insecure contexts; no-op
    }
  };

  const moreButton = (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      title={t("call.controls.moreControls")}
      aria-label={t("call.controls.moreControls")}
      aria-haspopup="menu"
      aria-expanded={open}
      className={
        isMobile
          ? `relative size-11 shrink-0 rounded-2xl border flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              open
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-foreground/[0.06] border-border text-foreground hover:bg-foreground/10"
            }`
          : `relative size-10 rounded-xl flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              open ? "bg-foreground/10 text-foreground" : "text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
            }`
      }
    >
      <MoreHorizontal className="size-5" />
    </button>
  );

  if (!isMobile) {
    return (
      <div ref={wrapRef} className="relative">
        {moreButton}
        {open && (
          <CompactOverflowMenu
            t={t}
            copied={copied}
            onCopy={() => void handleCopy()}
            screenShareLabel={screenShareLabel}
            screenSharing={screenSharing}
            screenShareDisabled={screenShareDisabled}
            screenShareNotSupported={screenShareNotSupported}
            onToggleScreenShare={() => {
              onToggleScreenShare();
              close();
            }}
            chatUnreadCount={chatUnreadCount}
            activePanel={activePanel}
            participantBadge={participantBadge}
            onTogglePanel={togglePanel}
            handRaised={handRaised}
            onToggleHandRaised={() => {
              onToggleHandRaised();
              close();
            }}
            viewMode={viewMode}
            onViewModeChange={(mode) => {
              onViewModeChange(mode);
              close();
            }}
            reactionEmojis={reactionEmojis}
            onEmitReaction={(emoji) => {
              onEmitReaction(emoji);
              close();
            }}
            pip={showPip ? { isOpen: pip!.isOpen, onToggle: togglePip } : null}
          />
        )}
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      {moreButton}
      {open && (
        <>
          <button
            type="button"
            aria-label={t("call.controls.sheetClose")}
            className="fixed inset-0 z-40 bg-black/30"
            onClick={close}
          />
          {/* The design's phone "more" bottom sheet (weco2/call.jsx `ic-sheet`). */}
          <div
            className="fixed left-0 right-0 bottom-0 z-50 bg-background border-t border-border rounded-t-3xl px-4 pt-3 pb-5 anim-sheet-up"
            role="dialog"
            aria-label={t("call.controls.moreActions")}
          >
            <div className="w-10 h-1.5 rounded-full bg-border mx-auto mb-4" aria-hidden />
            <div className="flex items-center gap-2 mb-3" role="tablist">
              {LAYOUT_TABS.map(({ mode, icon: Icon, shortKey }) => {
                const active = viewMode === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => onViewModeChange(mode)}
                    className={`flex-1 h-10 rounded-2xl border text-xs font-bold flex items-center justify-center gap-2 transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted/40 border-border text-foreground hover:bg-muted"
                    }`}
                  >
                    <Icon className="size-[15px]" />
                    {t(shortKey)}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between gap-1 mb-3 px-1">
              {reactionEmojis.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  aria-label={t("call.controls.reactWith", { emoji })}
                  onClick={() => {
                    onEmitReaction(emoji);
                    close();
                  }}
                  className="size-10 rounded-xl hover:bg-muted text-2xl flex items-center justify-center transition-colors"
                >
                  {emoji}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-4 gap-2">
              <SheetAction
                icon={screenSharing ? MonitorOff : MonitorUp}
                label={t("call.controls.shareScreen")}
                active={screenSharing}
                disabled={screenShareDisabled || screenShareNotSupported}
                onClick={() => {
                  if (screenShareNotSupported) return;
                  onToggleScreenShare();
                  close();
                }}
              />
              <SheetAction
                icon={Hand}
                label={handRaised ? t("call.controls.lowerHand") : t("call.controls.raiseHand")}
                active={handRaised}
                onClick={() => {
                  onToggleHandRaised();
                  close();
                }}
              />
              <SheetAction
                icon={Users}
                label={t("call.controls.peopleWithCount", { count: participantBadge })}
                active={activePanel === "users"}
                onClick={() => togglePanel("users")}
              />
              <SheetAction
                icon={Settings}
                label={t("call.controls.roomSettings")}
                disabled={!isHost}
                onClick={() => {
                  if (!isHost) return;
                  onOpenSettings?.();
                  close();
                }}
              />
              <SheetAction
                icon={MessageSquare}
                label={t("call.controls.chat")}
                active={activePanel === "chat"}
                badge={chatUnreadCount}
                onClick={() => togglePanel("chat")}
              />
              {RECORDING_ENABLED && (
                <SheetAction
                  icon={recording ? Square : Circle}
                  label={recording ? t("call.controls.stopRec") : t("call.controls.record")}
                  active={recording}
                  onClick={() => {
                    onToggleRecording?.();
                    close();
                  }}
                />
              )}
              {showPip && (
                <SheetAction
                  icon={PictureInPicture}
                  label={t("call.pip.open")}
                  active={pip!.isOpen}
                  onClick={togglePip}
                />
              )}
              <SheetAction icon={X} label={t("call.controls.sheetClose")} onClick={close} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SheetAction({
  icon: Icon,
  label,
  onClick,
  active,
  disabled,
  badge,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  badge?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active ?? undefined}
      className={`relative flex flex-col items-center gap-2 py-3 px-1 rounded-2xl border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        active ? "bg-primary/10 border-primary text-foreground" : "bg-muted/40 border-border text-foreground hover:bg-muted"
      }`}
    >
      <Icon className={`size-5 ${active ? "text-primary" : "text-foreground"} ${Icon === Hand && active ? "anim-wave" : ""}`} />
      <span className="text-[11px] font-bold leading-tight text-center">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="absolute top-1.5 end-1.5 min-w-4 h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-primary text-primary-foreground">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </button>
  );
}

function CompactOverflowMenu({
  t,
  copied,
  onCopy,
  screenShareLabel,
  screenSharing,
  screenShareDisabled,
  screenShareNotSupported,
  onToggleScreenShare,
  chatUnreadCount,
  activePanel,
  participantBadge,
  onTogglePanel,
  handRaised,
  onToggleHandRaised,
  viewMode,
  onViewModeChange,
  reactionEmojis,
  onEmitReaction,
  pip,
}: {
  t: TFunction;
  copied: boolean;
  onCopy: () => void;
  screenShareLabel: string;
  screenSharing: boolean;
  screenShareDisabled: boolean;
  screenShareNotSupported: boolean;
  onToggleScreenShare: () => void;
  chatUnreadCount: number;
  activePanel: SidePanelTab;
  participantBadge: number;
  onTogglePanel: (tab: NonNullable<SidePanelTab>) => void;
  handRaised: boolean;
  onToggleHandRaised: () => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  reactionEmojis: readonly string[];
  onEmitReaction: (emoji: string) => void;
  pip: { isOpen: boolean; onToggle: () => void } | null;
}) {
  return (
    <div
      className="absolute bottom-full end-0 mb-2 w-64 p-2 z-50 flex flex-col gap-1 bg-background border border-border rounded-2xl shadow-2xl anim-scale-in"
      role="menu"
      aria-label={t("call.controls.moreActions")}
    >
      {screenShareNotSupported ? (
        <Row
          label={t("call.controls.screenShareNotAvailableLabel")}
          sublabel={t("call.controls.screenShareNotAvailableSublabel")}
          icon={<MonitorUp className="size-5" />}
          disabled
          onClick={() => {}}
        />
      ) : (
        <Row
          label={screenShareLabel}
          icon={screenSharing ? <MonitorOff className="size-5" /> : <MonitorUp className="size-5" />}
          disabled={screenShareDisabled}
          onClick={onToggleScreenShare}
        />
      )}
      <Row
        label={
          chatUnreadCount > 0
            ? t("call.controls.toggleChatUnread", { count: chatUnreadCount })
            : t("call.controls.toggleChat")
        }
        icon={<MessageSquare className="size-5" />}
        badge={chatUnreadCount > 0 ? chatUnreadCount : undefined}
        active={activePanel === "chat"}
        onClick={() => onTogglePanel("chat")}
      />
      <Row
        label={t("call.controls.toggleParticipants")}
        icon={<Users className="size-5" />}
        badge={participantBadge > 0 ? participantBadge : undefined}
        active={activePanel === "users"}
        onClick={() => onTogglePanel("users")}
      />
      <Row
        label={handRaised ? t("call.controls.lowerHand") : t("call.controls.raiseHand")}
        icon={<Hand className={`size-5 ${handRaised ? "anim-wave" : ""}`} />}
        active={handRaised}
        onClick={onToggleHandRaised}
      />
      {pip && (
        <Row
          label={t("call.pip.open")}
          icon={<PictureInPicture className="size-5" />}
          active={pip.isOpen}
          onClick={pip.onToggle}
        />
      )}
      <div className="flex items-center gap-1 px-1 pt-1">
        <LayoutGrid className="size-4 shrink-0 text-muted-foreground" />
        {getLayoutOptions(t).map((opt) => (
          <button
            key={opt.mode}
            type="button"
            role="menuitemradio"
            aria-checked={viewMode === opt.mode}
            aria-label={opt.label}
            onClick={() => onViewModeChange(opt.mode)}
            className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              viewMode === opt.mode ? "bg-primary/10 text-primary" : "text-foreground"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-1 px-1 pt-1">
        {reactionEmojis.map((emoji) => (
          <button
            key={emoji}
            type="button"
            role="menuitem"
            aria-label={t("call.controls.reactWith", { emoji })}
            onClick={() => onEmitReaction(emoji)}
            className="size-9 rounded-xl flex items-center justify-center text-xl transition-colors hover:bg-muted"
          >
            {emoji}
          </button>
        ))}
      </div>
      <button
        type="button"
        role="menuitem"
        onClick={onCopy}
        aria-label={t("call.controls.copyMeetingLink")}
        className={`mt-1 w-full inline-flex items-center justify-center gap-2 h-10 rounded-2xl font-bold text-sm transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
          copied ? "bg-primary/15 text-primary" : "bg-primary text-primary-foreground"
        }`}
      >
        {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
        <span>{copied ? t("call.controls.copied") : t("call.controls.copyMeetingLink")}</span>
      </button>
    </div>
  );
}

interface RowProps {
  label: string;
  sublabel?: string;
  icon: ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  badge?: number;
}

function Row({ label, sublabel, icon, onClick, active, disabled, badge }: RowProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      aria-label={sublabel ? `${label}. ${sublabel}` : label}
      aria-pressed={active ?? undefined}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-start transition-colors hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        active ? "bg-primary/10 text-primary" : "text-foreground"
      }`}
    >
      <span className="shrink-0">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block truncate">{label}</span>
        {sublabel && <span className="block truncate text-xs font-normal text-muted-foreground">{sublabel}</span>}
      </span>
      {badge !== undefined && badge > 0 && (
        <span className="ml-auto min-w-4 h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-primary text-primary-foreground">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </button>
  );
}
